import { type FormEvent, type ReactNode, useState } from 'react';
import { t } from '../i18n';
import { Button } from './ui/Button';
import { Sheet } from './ui/Sheet';

/**
 * One-button confirm dialog.
 * Replaces window.confirm() with a styled, dismissable modal.
 */
export function ConfirmModal({
  title,
  body,
  confirmLabel,
  destructive,
  onConfirm,
  onClose,
}: {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet onClose={onClose} closeOnBackdrop>
      <h2 className="font-heading font-bold tracking-tight text-xl">{title}</h2>
      {body && <div className="text-sm text-foreground/80">{body}</div>}
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button
          variant={destructive ? 'danger' : 'primary'}
          onClick={() => {
            onConfirm();
            onClose();
          }}
        >
          {confirmLabel ?? t('Continue')}
        </Button>
      </div>
    </Sheet>
  );
}

/**
 * Multiline prompt modal. Returns the entered text via onSubmit, or null
 * if dismissed.
 */
export function PromptModal({
  title,
  placeholder,
  defaultValue,
  required = true,
  onSubmit,
  onClose,
}: {
  title: string;
  placeholder?: string;
  defaultValue?: string;
  required?: boolean;
  onSubmit: (value: string) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(defaultValue ?? '');
  function submit(e: FormEvent) {
    e.preventDefault();
    if (required && !value.trim()) return;
    onSubmit(value.trim());
    onClose();
  }
  return (
    <Sheet onClose={onClose} onSubmit={submit} closeOnBackdrop>
      <h2 className="font-heading font-bold tracking-tight text-xl">{title}</h2>
      <textarea
        // biome-ignore lint/a11y/noAutofocus: PromptModal is opened by an explicit user action and exists only to collect this one value, so focus belongs in the textarea
        autoFocus
        className="input min-h-[120px]"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button type="submit" disabled={required && !value.trim()}>
          {t('Continue')}
        </Button>
      </div>
    </Sheet>
  );
}
